/**
 * The marketing page. Assembly only — every claim, string and piece of state
 * lives in the section that owns it.
 *
 * ## The order is the argument
 *
 * Hero (with the phone doing the showing), then how it works, then the two
 * escalating linking sections — the mechanism on seven notes, the second brain
 * on a semester — then privacy as the foundation under both, the remaining
 * feature cards, ownership as one strip, the penciled-in future, and the
 * closing ask. The reader meets what the product does before being told what
 * it will not do with their data, and meets both before being asked for an
 * email a second time.
 *
 * ## The column
 *
 * `PageColumn` (shared with every route) is a centred `maxWidth: 1100` column
 * with **no horizontal padding of its own** — each section carries
 * `spacing.md` on the horizontal axis, and that inset is the page's whole
 * gutter at every width down to the 320 px reflow floor (FR-019(f)).
 *
 * ## Landmarks
 *
 * `<main>` wraps every section but **not** the footer: a `<footer>` nested in
 * `<main>` stops being the `contentinfo` landmark. As siblings, `banner` (the
 * header in `__root.tsx`), `main` and `contentinfo` are all top level, which
 * is what a screen reader's landmark list expects.
 */

import { createFileRoute } from "@tanstack/react-router";
import { View } from "react-native";
import { ClosingCta } from "@/components/marketing/closing-cta";
import { FeatureCards } from "@/components/marketing/feature-cards";
import { Footer } from "@/components/marketing/footer";
import { Hero } from "@/components/marketing/hero";
import { HowItWorks } from "@/components/marketing/how-it-works";
import { Later } from "@/components/marketing/later";
import { Ownership } from "@/components/marketing/ownership";
import { Privacy } from "@/components/marketing/privacy";
import { SecondBrain } from "@/components/marketing/second-brain";
import { SelfLinking } from "@/components/marketing/self-linking";
import { PageColumn } from "@/components/page-column";

export const Route = createFileRoute("/")({
  component: HomePage,
});

function HomePage() {
  return (
    <PageColumn>
      <View role="main">
        <Hero />
        <HowItWorks />
        <SelfLinking />
        <SecondBrain />
        <Privacy />
        <FeatureCards />
        <Ownership />
        <Later />
        <ClosingCta />
      </View>

      <Footer />
    </PageColumn>
  );
}
